import { useEffect, useId, useRef } from "react";
import { motion, useInView, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { GRID_MARK as M } from "./gridMarkPaths.js";

const EASE_OUT = [0.16, 1, 0.3, 1];
const [VX, VY, VW, VH] = M.viewBox.split(" ").map(Number);
const FLOOR = 770;
const [HX, HY] = M.headCenter;
const [SX, SY, EX, EY] = M.smileBox;
const SMILE = `M${SX + 3} ${SY + 2} Q ${(SX + EX) / 2} ${EY + 14} ${EX - 3} ${SY + 2}`;

function Part({ name }) {
  return <path transform={M[name].transform} d={M[name].d} />;
}

// The GRID wordmark as live vector parts. The letters rise from a floor, a person stands up
// inside the R, the arrow launches last, and the head follows the pointer.
export function GridMark({ className = "", title = "Project GRID" }) {
  const ref = useRef(null);
  const id = useId().replace(/:/g, "");
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const play = reduceMotion || inView;

  const lookX = useMotionValue(0);
  const lookY = useMotionValue(0);
  const headX = useSpring(lookX, { stiffness: 120, damping: 18, mass: 0.6 });
  const headY = useSpring(lookY, { stiffness: 120, damping: 18, mass: 0.6 });
  const lift = useMotionValue(0);
  const arrowLift = useSpring(lift, { stiffness: 220, damping: 22 });


  useEffect(() => {
    if (reduceMotion) return undefined;
    const onMove = (event) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box || box.bottom < 0 || box.top > window.innerHeight) return;
      const scale = box.width / VW;
      const cx = box.left + (HX - VX) * scale;
      const cy = box.top + (HY - VY) * scale;
      const dx = event.clientX - cx;
      const dy = event.clientY - cy;
      const distance = Math.hypot(dx, dy) || 1;
      const reach = Math.min(1, distance / 420);
      lookX.set((dx / distance) * 9 * reach);
      lookY.set((dy / distance) * 7 * reach);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [lookX, lookY, reduceMotion]);

  const rise = (delay, distance = 520) => (reduceMotion
    ? {}
    : {
      initial: { y: distance },
      animate: play ? { y: 0 } : { y: distance },
      transition: { duration: 1.1, delay, ease: EASE_OUT },
    });

  return (
    <svg
      ref={ref}
      className={`grid-mark ${className}`}
      viewBox={M.viewBox}
      role="img"
      aria-label={title}
      onPointerEnter={() => lift.set(-22)}
      onPointerLeave={() => lift.set(0)}
    >
      <defs>
        <clipPath id={`${id}-floor`}>
          <rect x={VX - 40} y={VY - 400} width={VW + 80} height={FLOOR - VY + 400} />
        </clipPath>
        <mask id={`${id}-person`} maskUnits="userSpaceOnUse" x={VX - 40} y={VY - 400} width={VW + 80} height={VH + 800}>
          <rect x={VX - 40} y={VY - 400} width={VW + 80} height={VH + 800} fill="#fff" />
          <g fill="#000">
            <motion.g {...rise(0.75, 260)}>
              <Part name="body" />
            </motion.g>
            <motion.g style={reduceMotion ? undefined : { x: headX, y: headY }}>
              <motion.g
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                initial={reduceMotion ? false : { scale: 0 }}
                animate={play ? { scale: 1 } : { scale: 0 }}
                transition={{ duration: 0.7, delay: 1.05, ease: EASE_OUT }}
              >
                <Part name="head" />
              </motion.g>
            </motion.g>
          </g>
        </mask>
      </defs>
      <g fill="currentColor" clipPath={`url(#${id}-floor)`}>
        <motion.g {...rise(0)}><Part name="G" /></motion.g>
        <motion.g {...rise(0.09)}>
          <g mask={`url(#${id}-person)`}><Part name="R" /></g>
        </motion.g>
        <motion.g {...rise(0.18)}><Part name="D" /></motion.g>
        <motion.g style={reduceMotion ? undefined : { y: arrowLift }}>
          <motion.g {...rise(0.42, 640)} transition={{ duration: 1.3, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}>
            <Part name="I" />
          </motion.g>
        </motion.g>
      </g>
      <motion.g style={reduceMotion ? undefined : { x: headX, y: headY }}>
        <motion.path
          d={SMILE}
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
          animate={play ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
          transition={{ duration: 0.6, delay: 1.45, ease: EASE_OUT }}
        />
      </motion.g>
    </svg>
  );
}
